import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, FileCode, HelpCircle, Printer } from 'lucide-react';
import { COLORS } from '../../data/mockData';
import { DOC_META, SECTIONS } from '../../data/architectureDocs';

const card = {
  backgroundColor: COLORS.surface,
  borderRadius: 16,
  padding: '22px 24px',
  border: `1px solid ${COLORS.border}`,
  boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  marginBottom: 16,
};
const h2 = {
  margin: '0 0 12px',
  fontSize: 22,
  fontWeight: 800,
  color: COLORS.textPrimary,
  fontFamily: 'Outfit, sans-serif',
};
const h3 = {
  margin: '20px 0 8px',
  fontSize: 16,
  fontWeight: 700,
  color: COLORS.textPrimary,
  fontFamily: 'Outfit, sans-serif',
};
const p = {
  margin: '0 0 10px',
  fontSize: 14,
  lineHeight: 1.6,
  color: COLORS.textPrimary,
};
const muted = { ...p, color: COLORS.textSecondary };
const th = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 700,
  color: COLORS.textSecondary,
  borderBottom: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.surfaceSecondary,
  whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px',
  fontSize: 13,
  lineHeight: 1.45,
  color: COLORS.textPrimary,
  borderBottom: `1px solid ${COLORS.border}`,
  verticalAlign: 'top',
};

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto', margin: '10px 0 14px', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{headers.map((h) => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j} style={td}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ kind = 'info', children }) {
  const palettes = {
    info: { bg: '#EEF5FB', border: '#B7D0E8', icon: CheckCircle2, color: COLORS.secondary },
    warn: { bg: '#FFF8E1', border: '#F0E0A0', icon: AlertTriangle, color: '#A65500' },
    danger: { bg: '#FDECEC', border: '#F0C0C0', icon: AlertTriangle, color: COLORS.error },
    unknown: { bg: '#F4F1EA', border: '#D9D0BE', icon: HelpCircle, color: COLORS.textSecondary },
  };
  const pal = palettes[kind] || palettes.info;
  const Icon = pal.icon;
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start', padding: 14,
      margin: '10px 0 14px', borderRadius: 12, backgroundColor: pal.bg, border: `1px solid ${pal.border}`,
    }}>
      <Icon size={18} color={pal.color} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ fontSize: 13, lineHeight: 1.55, color: COLORS.textPrimary }}>{children}</div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={`arch-${id}`} style={card}>
      <h2 style={h2}>{title}</h2>
      {children}
    </section>
  );
}

export default function DocumentationTab() {
  const [filter, setFilter] = useState('');

  const jump = (id) => {
    const el = document.getElementById(`arch-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const visible = SECTIONS.filter((s) =>
    !filter.trim() || s.title.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <div data-testid="admin-architecture" style={{ maxWidth: 1100 }}>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <FileCode size={22} color={COLORS.primary} />
            <h2 style={{ ...h2, margin: 0 }}>Website Architecture and Source Code</h2>
          </div>
          <p style={muted}>
            Operations handbook compiled from the repository on {DOC_META.lastReviewed}.
            Secrets, bank accounts, invoices, and vendor-team memberships are not stored in
            this codebase — those rows are marked <strong>Not in repository</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          style={{
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surfaceSecondary, color: COLORS.textPrimary,
            fontWeight: 600, cursor: 'pointer', fontSize: 13,
          }}
        >
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>

      <div style={{ ...card, position: 'sticky', top: 8, zIndex: 5 }}>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter sections…"
          style={{
            width: '100%', maxWidth: 360, padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${COLORS.border}`, marginBottom: 12, fontSize: 13,
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {visible.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => jump(s.id)}
              style={{
                padding: '6px 12px', borderRadius: 999, border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.surfaceSecondary, color: COLORS.textPrimary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {s.title}
            </button>
          ))}
        </div>
      </div>

      <Section id="architecture" title="Website Architecture and Source Code">
        <h3 style={h3}>Where the complete source code is stored</h3>
        <p style={p}>
          The complete application lives in a single Git repository. Production deploys
          from the <code>main</code> branch via Dokploy. There is no separate private
          monorepo, design-system repo, or compiled-only distribution.
        </p>
        <Table
          headers={['Item', 'Value']}
          rows={[
            ['Repository', <a href={DOC_META.repoUrl} target="_blank" rel="noreferrer">{DOC_META.repoUrl}</a>],
            ['Owner / GitHub account', DOC_META.repoOwner],
            ['Default branch', DOC_META.defaultBranch],
            ['Canonical GitHub name', 'thriveLonger5_30_26 (capital L); thrivelonger5_30_26 also resolves'],
            ['Local workspace', 'Documents/thrivelonger5_30_26'],
            ['Layout', 'backend/ (FastAPI), frontend/ (React), docker-compose.dokploy.yml, DEPLOYMENT.md'],
          ]}
        />

        <h3 style={h3}>Programming languages, frameworks, databases, and major packages</h3>
        <Table
          headers={['Layer', 'Stack']}
          rows={[
            ['Frontend', 'React 19, Create React App + CRACO, React Router 7, Tailwind 3.4, Radix UI, axios, Three.js, Uppy/TUS, lucide-react, Recharts'],
            ['Backend', 'Python 3, FastAPI 0.110, Uvicorn, bcrypt, PyJWT, cryptography, APScheduler, httpx'],
            ['Database', 'PostgreSQL 16. Document API emulated in core/pgmongo.py (JSONB tables + BYTEA GridFS)'],
            ['AI', 'Official OpenAI Python SDK (gpt-4o, gpt-4o-mini, tts-1, whisper-1). litellm and google-genai are installed but unused'],
            ['Payments', 'Official Stripe Python SDK 15.0 — Checkout Sessions only'],
            ['Email', 'SendGrid HTTP API via httpx (not the SendGrid SDK)'],
            ['PHI / docs', 'AES-256-GCM (phi_crypto), pypdf, python-docx, reportlab, pillow'],
          ]}
        />

        <h3 style={h3}>Major sections of the code and how they work together</h3>
        <p style={p}>
          The React SPA talks only to <code>/api</code> on the FastAPI backend. FastAPI
          routers write Mongo-style queries that <code>core/pgmongo.py</code> stores as
          PostgreSQL JSONB. Large files (PHI, seminar videos) go to BYTEA chunk tables.
          OpenAI, Stripe, and SendGrid keys load from environment variables at boot and
          can be overridden at runtime from Admin → Integrations (encrypted in Postgres).
        </p>
        <Table
          headers={['Area', 'Key files', 'Role']}
          rows={[
            ['App bootstrap', 'backend/server.py', 'Mounts routers, CORS, seeds admin/investor, starts cron jobs'],
            ['Config', 'backend/core/config.py', 'Reads env: DATABASE_URL, JWT_SECRET, ADMIN_*, PHI_*, LAUNCH_DATE'],
            ['Auth', 'backend/routes/auth.py, core/deps.py', 'Register, login, JWT cookie, password reset'],
            ['Payments', 'backend/routes/payments.py, core/stripe_checkout.py', 'One-time Stripe Checkout for memberships and store items'],
            ['AI coach / help', 'backend/routes/chat.py, core/ai.py', 'GPT-4o chat with system prompts; Elite-gated coach'],
            ['PHI vault', 'backend/routes/phi/*, core/phi_crypto.py', 'Consent, templates, encrypted upload, signed downloads, audit'],
            ['Store + affiliates', 'backend/routes/store.py, affiliates.py', 'Seed catalog + admin products + Amazon affiliate links'],
            ['Admin APIs', 'backend/routes/admin.py, integrations.py', 'Users, research, blasts, products, integrations'],
            ['Frontend shell', 'frontend/src/App.js, SiteHeader.jsx, DashboardLayout.jsx', 'Public vs member vs admin routing and pre-launch gates'],
            ['Admin UI', 'frontend/src/pages/AdminPage.jsx + pages/admin/*', 'This dashboard, including this handbook'],
          ]}
        />

        <h3 style={h3}>What is completed, unfinished, or not working as labeled</h3>
        <Callout kind="warn">
          <strong>Known functional gaps.</strong> Marketing copy and some APIs describe
          features that are not actually implemented. Treat the table below as the
          current truth, not the landing-page copy.
        </Callout>
        <Table
          headers={['Item', 'Status']}
          rows={[
            ['Public site, member dashboard, admin panel', 'Working'],
            ['Email/password auth, password reset', 'Working (reset emails mock if SendGrid key is empty)'],
            ['PostgreSQL + PHI encryption + HIPAA consent + templates', 'Working'],
            ['Stripe Checkout (one-time charge)', 'Wired; live only if a real Stripe key is configured'],
            ['Admin Integrations (OpenAI / Stripe / SendGrid)', 'Working'],
            ['Amazon affiliate products in Store', 'Working (manual admin entry)'],
            ['Pre-launch lockdown until Oct 15, 2026', 'Working; admins and investor account bypass'],
            ['Recurring Stripe subscriptions', 'Not implemented — Checkout uses mode: payment (one-time)'],
            ['Refunds, dunning, failed-payment retry', 'Not implemented'],
            ['SMS / Twilio', 'Marketing copy only — no backend'],
            ['Google / Apple login', 'Removed / not wired'],
            ['Real wearable OAuth (Apple Health, Fitbit, Garmin)', 'Mock data only'],
            ['Apple Pay, Google Pay, ACH', 'Not configured in code'],
            ['eBay affiliates', 'Not present'],
            ['2FA, login lockout, rate limiting', 'Not implemented'],
            ['Live expert consultations', 'Marketing “coming soon” only'],
            ['Membership prices / tier numbers', 'Inconsistent across payments.py, membership.py, and landing copy'],
            ['Legacy POST /api/cart/checkout', 'Creates a “confirmed” order with no payment'],
          ]}
        />

        <h3 style={h3}>How to change the site and deploy an update</h3>
        <ol style={{ ...p, paddingLeft: 20 }}>
          <li>Clone or pull <code>main</code> from GitHub.</li>
          <li>Edit frontend and/or backend. Do not commit <code>.env</code>, API keys, or <code>ADMIN_PASSWORD</code>.</li>
          <li>Run backend tests from <code>backend/</code> with <code>pytest</code> when you have Postgres and <code>ADMIN_PASSWORD</code> set.</li>
          <li>Commit and push to <code>main</code> (or merge a PR into <code>main</code>).</li>
          <li>In Dokploy, open the Compose service and click <strong>Deploy</strong> (or wait for auto-deploy if enabled).</li>
          <li>Changing <code>BACKEND_PUBLIC_URL</code> requires a rebuild — it is baked into the React bundle at image build time.</li>
          <li>OpenAI / Stripe / SendGrid keys can be changed live under Admin → Integrations without a redeploy.</li>
        </ol>
      </Section>

      <Section id="hosting" title="Hosting and Deployment">
        <h3 style={h3}>Where the website and application are hosted</h3>
        <p style={p}>
          Production is intended to run as a Dokploy Compose project on a Hostinger VPS.
          One Docker image serves the React static site (port 3000) and FastAPI (port 8001).
          PostgreSQL 16 runs as a sibling container on a private Docker network.
          Traefik (bundled with Dokploy) terminates TLS and routes hostnames.
        </p>
        <Table
          headers={['Component', 'Where it runs']}
          rows={[
            ['Frontend + backend containers', 'Hostinger VPS via Dokploy Compose (docker-compose.dokploy.yml)'],
            ['PostgreSQL 16', 'Same VPS, private network only, volume pg_data'],
            ['TLS / reverse proxy', 'Dokploy Traefik + Let’s Encrypt'],
            ['Source of truth for deploys', 'GitHub main branch'],
            ['Local development', 'docker-compose.yml or uvicorn + CRA on a developer machine'],
          ]}
        />

        <h3 style={h3}>Dokploy access and how it is used</h3>
        <p style={p}>
          Dokploy is the deployment control plane on the VPS. ThriveLonger uses it as a
          <strong> Docker Compose</strong> application (not a Nixpacks/single-container app):
        </p>
        <ul style={{ ...p, paddingLeft: 20 }}>
          <li>Project source: GitHub → this repository, branch <code>main</code></li>
          <li>Compose path: <code>./docker-compose.dokploy.yml</code> (not <code>dockploy</code>)</li>
          <li>Services named <code>frontend</code>, <code>backend</code>, <code>postgres</code> so Domains can be attached</li>
          <li>No host ports published — Traefik reaches containers on <code>dokploy-network</code></li>
          <li>Environment variables and Let’s Encrypt domains are set in the Dokploy UI</li>
        </ul>
        <Callout kind="unknown">
          <strong>Dokploy login URL and who has panel access</strong> are not stored in
          the repo. In the last DNS snapshot, an A record for <code>panel</code> pointed
          at the VPS, which is the usual Dokploy panel host (<code>panel.thrivelonger.ai</code>
          or the VPS IP). Confirm in Hostinger DNS and the Dokploy Users screen.
        </Callout>

        <h3 style={h3}>Underlying server / cloud provider</h3>
        <p style={p}>
          <strong>Hostinger VPS</strong> (not AWS/GCP/Azure). Last observed public IPv4:
          <code> 177.7.57.207</code>. Confirm current IP in Hostinger hPanel → VPS.
        </p>

        <h3 style={h3}>Server specifications and current costs</h3>
        <Callout kind="unknown">
          CPU, RAM, disk, Hostinger plan name, and the current monthly invoice are
          <strong> not in this repository</strong>. Open Hostinger hPanel → VPS → the
          ThriveLonger server for specs and billing. Typical Hostinger KVM plans that
          comfortably run Dokploy + Postgres are roughly $7–30/month; that is an
          industry range, not this account’s invoice.
        </Callout>

        <h3 style={h3}>Deployment process</h3>
        <ol style={{ ...p, paddingLeft: 20 }}>
          <li>Push to GitHub <code>main</code>.</li>
          <li>Dokploy pulls and runs <code>docker compose -f docker-compose.dokploy.yml up -d --build</code>.</li>
          <li>Frontend image is built with <code>REACT_APP_BACKEND_URL=$BACKEND_PUBLIC_URL</code>.</li>
          <li>Backend starts uvicorn, seeds admin + investor, loads integration settings, starts APScheduler.</li>
          <li>Traefik routes <code>www.thrivelonger.ai</code> → frontend:3000 and <code>/api</code> (or api subdomain) → backend:8001.</li>
        </ol>

        <h3 style={h3}>Development, staging, and production</h3>
        <p style={p}>
          There is <strong>no separate staging environment</strong> in the repo or compose
          files. Developers run locally. Production is the Dokploy stack on the VPS.
          To create staging: duplicate the Dokploy service on a hostname such as
          <code>staging.thrivelonger.ai</code> with its own Postgres volume and
          <code>LAUNCH_DATE</code> in the past if you need signups unlocked.
        </p>

        <h3 style={h3}>Backups and restoration</h3>
        <p style={p}>
          All application data, including encrypted PHI files, lives in the Docker
          volume <code>pg_data</code>. DEPLOYMENT.md recommends scheduling
          <code>pg_dump</code> (Dokploy can snapshot compose volumes).
        </p>
        <Table
          headers={['Task', 'How']}
          rows={[
            ['Backup', 'Dokploy scheduled volume backup, or: docker exec postgres pg_dump -U thrive thrivelonger > backup.sql'],
            ['Restore', 'Stop backend, restore dump into the postgres container, restart backend'],
            ['PHI note', 'Dump contains ciphertext only if PHI_MASTER_KEY is intact. Losing PHI_MASTER_KEY makes medical files unreadable'],
          ]}
        />
        <Callout kind="unknown">
          Whether a Dokploy/Hostinger backup schedule is actually enabled on the live
          VPS cannot be confirmed from source control. Verify in Dokploy → Backups
          and Hostinger snapshots.
        </Callout>

        <h3 style={h3}>Scheduled jobs and background services</h3>
        <Table
          headers={['Job', 'When', 'What it does']}
          rows={[
            ['Auto content blast', 'Configurable cron; default Mon/Wed/Fri 09:00 UTC', 'Emails research blasts to members at or above min_tier'],
            ['Group chat weekly digest', 'Sunday 09:00 UTC', 'Emails admins a moderation/activity report'],
            ['TTL sweeper', 'Every 60 seconds', 'Deletes expired password-reset tokens and abandoned PHI uploads'],
            ['PHI AI summary', 'On upload (async)', 'Decrypts a text extract and asks GPT-4o for 3 bullets'],
          ]}
        />
      </Section>

      <Section id="dns" title="Domain and DNS">
        <Table
          headers={['Question', 'Answer from repository / last ops session']}
          rows={[
            ['Registered domain', 'thrivelonger.ai'],
            ['Registrar', 'Not in repository — check whois and the invoice (often Hostinger or the account that paid for .ai)'],
            ['Registrar account owner', 'Not in repository — whoever can log into the registrar'],
            ['DNS provider', 'Last configured in Hostinger DNS (same VPS account). Confirm in hPanel → Domains → DNS'],
            ['SSL', 'Let’s Encrypt via Dokploy Traefik. Certificates issue on first HTTPS request after the domain is attached'],
          ]}
        />
        <h3 style={h3}>DNS records last observed</h3>
        <p style={muted}>
          These were reported during the August 2026 deploy session. They may have
          changed — treat Hostinger DNS as authoritative.
        </p>
        <Table
          headers={['Type', 'Name', 'Value', 'TTL', 'Purpose']}
          rows={[
            ['A', '@', '177.7.57.207', '300', 'Apex thrivelonger.ai → VPS (was serving Dokploy login until Traefik/www routing was corrected)'],
            ['A', 'panel', '177.7.57.207', '14400', 'Dokploy control panel'],
            ['CNAME', 'www', 'thrivelonger.ai', '14400', 'www → apex'],
            ['A or CNAME', 'api (optional)', 'VPS IP or www', '—', 'Only if using a separate API hostname'],
          ]}
        />
        <h3 style={h3}>Subdomains</h3>
        <Table
          headers={['Host', 'Intended use', 'Status']}
          rows={[
            ['www.thrivelonger.ai', 'Public website', 'Configured in DEPLOYMENT.md'],
            ['thrivelonger.ai', 'Apex — should redirect or serve the site, not the Dokploy login', 'Must be attached in Dokploy Domains'],
            ['panel.thrivelonger.ai', 'Dokploy admin UI', 'DNS present historically'],
            ['api.thrivelonger.ai', 'Optional dedicated API host', 'Documented, not required if /api is proxied on www'],
            ['staging.*', 'Not created', 'Reserved conceptually only'],
          ]}
        />
      </Section>

      <Section id="database" title="Database and User Information">
        <Table
          headers={['Question', 'Answer']}
          rows={[
            ['Database system', 'PostgreSQL 16'],
            ['Where hosted', 'Same Hostinger VPS, Docker service postgres, volume pg_data, not reachable from the public internet'],
            ['Admin access', 'POSTGRES_USER (default thrive) + POSTGRES_PASSWORD from Dokploy Environment. App access via DATABASE_URL'],
            ['GUI', 'None bundled. Use docker exec + psql, or tunnel a GUI'],
          ]}
        />

        <h3 style={h3}>Information collected from users and where it is stored</h3>
        <Table
          headers={['Data', 'Collection / tables', 'Notes']}
          rows={[
            ['Name, email, password hash, role, membership tier, trial', 'c_users', 'Password is bcrypt, never plaintext'],
            ['Session JWT', 'Browser cookie session_token (not a DB session row for JWT path)'],
            ['Password-reset tokens', 'c_password_reset_tokens', 'SHA-256 hash, 1-hour TTL'],
            ['Health survey + scores + plans + goals', 'c_users, c_health_scores, c_health_plans, c_goals'],
            ['AI coach transcripts', 'c_chat_messages', 'User text + model replies'],
            ['Cart, orders, payment session metadata', 'c_cart_items, c_orders, c_payment_transactions', 'No card numbers'],
            ['Wearable “sync”', 'c_wearable_devices, c_wearable_data', 'Currently mock-generated'],
            ['Body composition / biological_age field', 'c_scale_measurements'],
            ['Medical records (PHI)', 'c_phi_records + gf_phi_files_* BYTEA', 'AES-256-GCM envelope encryption'],
            ['HIPAA consent', 'c_phi_consents'],
            ['PHI audit trail', 'c_phi_audit_log'],
            ['Group chat', 'c_group_chat_*'],
            ['Email send log', 'c_email_notifications'],
            ['Integration secrets', 'c_integration_settings', 'AES-256-GCM under PHI_MASTER_KEY'],
          ]}
        />

        <h3 style={h3}>Encryption</h3>
        <ul style={{ ...p, paddingLeft: 20 }}>
          <li>In transit: TLS 1.2+ via Traefik / Let’s Encrypt.</li>
          <li>PHI at rest: per-file AES-256-GCM; data-encryption key wrapped by <code>PHI_MASTER_KEY</code> (32 bytes, base64).</li>
          <li>Integration API keys at rest: same master key, AES-256-GCM.</li>
          <li>Passwords: bcrypt. Reset tokens: SHA-256.</li>
          <li>PHI downloads: HMAC-signed URLs, 60–900 second lifetime.</li>
        </ul>

        <h3 style={h3}>Medical / health / PII</h3>
        <p style={p}>
          The Medical Vault is the PHI store. Members must accept HIPAA policy version
          2026-08-01 before downloading templates or uploading records. Uploads are
          encrypted before they hit BYTEA storage. AI summaries send a <em>decrypted
          text extract</em> (capped) to OpenAI — not the raw file bytes — and store
          only three bullets. Admins can view the audit log and perform bulk export
          from Admin → Medical Vault. Treat that tab as access to protected health
          information.
        </p>

        <h3 style={h3}>Test accounts and test data</h3>
        <Table
          headers={['Account', 'Email', 'Role', 'Notes']}
          rows={[
            ['Site administrator', 'ADMIN_EMAIL env (default roberthcohenmd@gmail.com)', 'admin', 'Password is ADMIN_PASSWORD in Dokploy — not in git'],
            ['Prospective investor', 'prospectiveinvestor@synul8.com', 'user, Elite, prelaunch_access', 'Password GreatPotential_1 is hardcoded in backend/server.py (security debt)'],
            ['Pytest users', 'test_user_*@example.com', 'user', 'Created only when the test suite runs'],
          ]}
        />
        <Callout kind="danger">
          The investor password is in source control. Rotate it and move it to an
          environment variable before inviting anyone else to the repo.
        </Callout>
      </Section>

      <Section id="stripe" title="Stripe and Payment Processing">
        <Callout kind="unknown">
          <strong>This repository cannot grant or verify Stripe ownership.</strong> Log
          into <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">dashboard.stripe.com</a>
          with the ThriveLonger business email, open Settings → Team, and confirm you
          are Owner. If you are only a Developer or Analyst, ask the current Owner to
          transfer the account. Bank payouts are Settings → Payouts / Bank accounts
          and are not readable from this app.
        </Callout>
        <Table
          headers={['Question', 'What the code shows', 'What you must verify in Stripe']}
          rows={[
            ['Which Stripe account is connected', 'Whatever STRIPE_API_KEY / Admin → Integrations key is live (test keys start sk_test_, live keys sk_live_)', 'Dashboard home → account name and country'],
            ['Owner / administrator access', 'Not stored here', 'Settings → Team'],
            ['Payout bank account', 'Not stored here', 'Settings → Payouts'],
            ['Who else has Stripe admin access', 'Not stored here', 'Settings → Team'],
            ['Products configured in Stripe Dashboard', 'None required — prices are created inline on each Checkout Session', 'Confirm no leftover Dashboard Products if you expected a catalog'],
            ['Do subscriptions recur?', 'No. mode is "payment" (one-time). Labels say “monthly” but Stripe will not bill again', 'Do not enable a Dashboard subscription unless you change the code'],
            ['Cancellations', 'No Stripe subscription to cancel. Admin can lower membership_tier on the Users tab', 'Refunds (if any) must be issued in the Stripe Dashboard'],
            ['Refunds', 'No refund API in the app', 'Payments → select charge → Refund'],
            ['Failed payment', 'Session stays unpaid; membership is not upgraded. No retry emails', 'Stripe emails the customer only if you enable that in Stripe settings'],
            ['Where the card is entered', 'Stripe-hosted Checkout page (redirect). Card fields are not on thrivelonger.ai', '—'],
            ['Does ThriveLonger store raw cards?', 'No. Only session_id, amount, status, and metadata in c_payment_transactions', 'PCI SAQ-A style Checkout'],
            ['Webhooks', 'POST /api/webhook/stripe; requires STRIPE_WEBHOOK_SECRET', 'Developers → Webhooks must point at https://<public-host>/api/webhook/stripe'],
            ['API keys', 'STRIPE_API_KEY + webhook secret in env or Admin → Integrations', 'Rotate in Stripe → Developers → API keys'],
            ['Apple Pay / Google Pay / ACH', 'Not enabled in Checkout session create()', 'Payment methods in Dashboard are unused until code requests them'],
          ]}
        />
        <h3 style={h3}>Membership packages defined in code</h3>
        <Table
          headers={['Package ID', 'Name', 'Amount charged (one-time)', 'User tier written on success']}
          rows={[
            ['tier_2_monthly', 'Analyst', '$29.99', '2'],
            ['tier_3_monthly', 'Optimizer', '$69.99', '3'],
            ['tier_4_monthly', 'Elite', '$149.99', '4'],
          ]}
        />
        <Callout kind="warn">
          Landing-page and <code>membership.py</code> still advertise Explorer / Analyst
          $29.99 / Optimizer $79.99 / Elite $199.99 with different tier numbers. The
          Stripe charge amounts above are what Checkout actually bills.
        </Callout>
      </Section>

      <Section id="apis" title="Every API and Outside Service">
        <p style={muted}>
          Account owner, billing email, plan, renewal date, and payment method for
          each vendor live in that vendor’s billing portal — not in git. Fill those
          columns from invoices.
        </p>
        <Table
          headers={['Service', 'Used for', 'Account / email in repo', 'Plan / cost', 'Grows with traffic?']}
          rows={[
            ['OpenAI', 'AI coach, help chat, seminars, TTS, Whisper, PHI summaries, group-chat moderation', 'API key only; owner email not in repo', 'Pay-as-you-go. $0 if key unused', 'Yes — tokens'],
            ['Stripe', 'Checkout + webhooks', 'Key only', 'Stripe processing fees on successful charges; no monthly platform fee in code', 'Yes — per charge'],
            ['SendGrid', 'Transactional + blast email', 'Default from noreply@thrivelonger.ai; key optional', 'Free tier then paid. Mock mode if no key', 'Yes — emails sent'],
            ['Twilio', 'Advertised SMS — not integrated', '—', '$0 today', 'N/A'],
            ['Google Fonts', 'Outfit, Work Sans, Inter loaded from fonts.googleapis.com', 'No Google Cloud project in repo', 'Free', 'No'],
            ['Google / Apple sign-in', 'Not connected', '—', '$0', 'N/A'],
            ['Apple Health / Fitbit / Garmin', 'UI only; mock sync', '—', '$0', 'N/A'],
            ['Amazon Associates', 'Manual affiliate URLs in Admin → Affiliates', 'Associate tag not stored as a secret — pasted into each URL', 'Commission from Amazon, no platform fee', 'Clicks/sales'],
            ['eBay Partner Network', 'Not connected', '—', '$0', 'N/A'],
            ['GitHub', 'Source + Dokploy git pull', 'ltcastro/thriveLonger5_30_26', 'Free for this private/public plan unless you bought extras', 'No'],
            ['Hostinger VPS', 'Compute, Docker, public IP', 'hPanel login not in repo', 'Monthly VPS invoice', 'Only if you upgrade the plan'],
            ['Dokploy', 'Deploy UI, Traefik, Let’s Encrypt', 'Self-hosted on the VPS', 'Open source — $0 license', 'No'],
            ['Let’s Encrypt', 'TLS certificates', 'Issued to attached domains', 'Free', 'No'],
            ['PostgreSQL', 'Primary database (self-hosted)', 'Local container', 'Included in VPS', 'Disk/RAM as data grows'],
            ['PostHog', 'Removed from index.html', '—', '$0', 'N/A'],
            ['Emergent LLM / OAuth', 'Removed', '—', '$0', 'N/A'],
            ['File / image CDN', 'None — media is in the frontend image or Postgres BYTEA', '—', 'VPS disk', 'Yes if seminar/PHI uploads grow'],
            ['Customer support desk', 'None (no Zendesk/Intercom)', 'Contact copy on the site only', '$0', 'N/A'],
            ['Monitoring / APM', 'None (no Datadog/Sentry in app)', 'Dokploy/container logs only', '$0 unless you add one', 'N/A'],
            ['Biological-age API', 'None. biological_age is a field on body-composition records', '—', '$0', 'N/A'],
          ]}
        />
      </Section>

      <Section id="expenses" title="Recurring and Usage-Based Expenses">
        <Table
          headers={['Expense', 'Type', 'Zero-user cost', 'Grows with members?']}
          rows={[
            ['Hostinger VPS', 'Monthly hosting', 'Whatever the VPS plan bills (confirm in hPanel)', 'Only if you resize the server'],
            ['thrivelonger.ai domain', 'Annual', 'Prorated ~$1–2/mo typical for .ai (confirm registrar invoice)', 'No'],
            ['Let’s Encrypt / Dokploy / Postgres / GitHub (basic)', 'Included or free', '$0 extra', 'Disk for uploads'],
            ['OpenAI', 'Usage', '$0 if nobody uses AI features', 'Yes — coach, seminars, PHI summary, TTS'],
            ['SendGrid', 'Usage / plan', '$0 on mock or free tier', 'Yes — password resets, blasts, digests'],
            ['Stripe fees', 'Per successful charge (~2.9% + $0.30 US cards, confirm your pricing)', '$0 with no sales', 'Yes'],
            ['Twilio / SMS', 'Not enabled', '$0', 'Would grow if implemented'],
            ['Apple/Google developer, wearable APIs, support desk, Sentry', 'Not purchased in code', '$0', 'N/A'],
            ['Software licenses (Figma/Adobe/Canva)', 'None referenced', 'Unknown if personal accounts exist outside the repo', 'N/A'],
          ]}
        />
        <Callout kind="info">
          <strong>Approximate idle monthly cost:</strong> essentially the Hostinger VPS
          plus the domain’s monthly share — typically on the order of <em>tens of
          dollars, not hundreds</em>, if OpenAI/SendGrid/Stripe are idle. Exact number
          requires the Hostinger invoice. The first costs to rise with membership are
          OpenAI tokens, SendGrid email volume, Stripe fees, and VPS disk for PHI/videos.
        </Callout>
      </Section>

      <Section id="email" title="Emails and Communications">
        <Table
          headers={['Address / channel', 'Where defined', 'Purpose']}
          rows={[
            ['noreply@thrivelonger.ai', 'SENDGRID_FROM_EMAIL default', 'Transactional From: address'],
            ['roberthcohenmd@gmail.com', 'ADMIN_EMAIL default', 'Administrator login; receives group-chat digest if that job emails admins'],
            ['prospectiveinvestor@synul8.com', 'seed_investor()', 'Evaluation login, not a mailbox we host'],
            ['Contact / careers copy', 'Info and About pages', 'Public content — no ticket system behind it'],
          ]}
        />
        <p style={p}>
          ThriveLonger does <strong>not host mailboxes</strong> (no Google Workspace /
          Microsoft 365 integration in this stack). SendGrid only sends. Inbound mail
          for @thrivelonger.ai depends on MX records at the registrar — not configured
          in this repo.
        </p>
        <h3 style={h3}>Automated emails currently coded</h3>
        <Table
          headers={['Type', 'Trigger', 'Works without SendGrid?']}
          rows={[
            ['password_reset', 'Forgot password', 'Logged as delivered_mock only'],
            ['content_blast / auto_blast', 'Admin send or Mon/Wed/Fri cron', 'Mock if no key'],
            ['weekly_digest, score_change, goal_reminder', 'Manual / test triggers', 'Mock if no key'],
            ['group_chat_weekly_report', 'Sunday cron', 'Mock if no key'],
            ['Marketing ESP (Mailchimp etc.)', 'Not configured', '—'],
          ]}
        />
        <p style={p}>
          SMS: no Twilio client, no site phone number in code. Contact-form: no
          dedicated form-to-inbox route beyond page copy. Password reset: token emailed
          (or mocked) with a 1-hour link to <code>/reset-password</code>.
        </p>
      </Section>

      <Section id="accounts" title="User Accounts and Administration">
        <h3 style={h3}>How a new user registers</h3>
        <p style={p}>
          <code>/register</code> posts name, email, and password to
          <code> POST /api/auth/register</code>. Until the launch date set by
          <code> LAUNCH_DATE</code> (currently 2026-10-15, announced publicly as the
          4th Quarter 2026) this returns 403
          for the public. New users would receive membership_tier 2, a 7-day trial,
          role <code>user</code>, and a JWT cookie (<code>session_token</code>, 7 days,
          HttpOnly).
        </p>
        <h3 style={h3}>Login and password recovery</h3>
        <p style={p}>
          Login is email + password (bcrypt). Forgot-password always returns success
          (no email enumeration), stores a hashed token, and emails a reset link.
          There is no social login and no 2FA.
        </p>
        <h3 style={h3}>How you access the administrator area</h3>
        <ol style={{ ...p, paddingLeft: 20 }}>
          <li>Sign in at <code>/login</code> with <code>ADMIN_EMAIL</code> / <code>ADMIN_PASSWORD</code>.</li>
          <li>Open Dashboard → Admin (sidebar link visible only when <code>role === "admin"</code>).</li>
          <li>Non-admins see Access Denied on <code>/dashboard/admin</code>.</li>
        </ol>
        <h3 style={h3}>What an administrator can see and change</h3>
        <Table
          headers={['Tab', 'Can do']}
          rows={[
            ['Overview', 'Counts: users, signups, orders, revenue, goals, chats, survey rate, admin count'],
            ['Users', 'List, edit name/role/tier, delete user'],
            ['Research', 'CRUD articles, import, scrape URL, upload files'],
            ['Content Blasts', 'Send email blasts; configure and test the cron schedule'],
            ['Orders', 'View orders'],
            ['Products', 'Add/delete custom store products (not the 8 seed items)'],
            ['Affiliates', 'CRUD Amazon/other affiliate products'],
            ['Integrations', 'Set OpenAI, Stripe, SendGrid URLs and secrets; test connection'],
            ['Medical Vault', 'Audit log, bulk ZIP/CSV, upload PHI on behalf of a member'],
            ['Group Chat', 'View and run weekly digest'],
            ['Architecture (this tab)', 'Read-only operations handbook'],
          ]}
        />
        <p style={p}>
          There is no suspend-user flag. Deleting a user is a hard delete. Changing
          role to admin here will persist until the next backend restart, when
          <code>seed_admin</code> demotes every admin except <code>ADMIN_EMAIL</code>.
        </p>
        <h3 style={h3}>Membership levels and how they affect access</h3>
        <Table
          headers={['Stored tier on user', 'Intended name', 'Access notes']}
          rows={[
            ['0 / 1', 'Free / Explorer (labels conflict)', 'Research-style content'],
            ['2', 'Analyst in Stripe; Optimizer in membership.py', 'New registrants are seeded at 2'],
            ['3', 'Optimizer in Stripe; Elite in membership.py', '—'],
            ['4', 'Elite in Stripe, admin, and investor seed', 'AI Coach and plan generation require tier >= 4'],
          ]}
        />
        <p style={p}>
          Subscription status is a field on the user document plus optional
          <code>trial_active</code>. Because Stripe charges are one-time, the site
          will not automatically downgrade someone next month.
        </p>
        <h3 style={h3}>Super-admin / developer accounts</h3>
        <p style={p}>
          Only one admin is maintained: <code>ADMIN_EMAIL</code>. The investor account
          is a full member with <code>prelaunch_access</code>, not an admin. No
          separate “developer” role exists.
        </p>
      </Section>

      <Section id="ai" title="AI Functionality">
        <Table
          headers={['Model', 'Where used', 'Prompt location']}
          rows={[
            ['gpt-4o', 'AI Health Coach, help assistant, plans, seminar scripts, PHI 3-bullet summary', 'backend/routes/chat.py (HEALTH_SYSTEM_PROMPT, HELP_SYSTEM_PROMPT); seminars.py; core/phi_summary.py'],
            ['gpt-4o-mini', 'Group chat moderation JSON', 'backend/routes/group_chat.py'],
            ['tts-1 / tts-1-hd', 'Seminar spoken audio', 'backend/routes/seminars.py'],
            ['whisper-1', 'Lip-sync word alignment from generated MP3', 'backend/routes/seminars.py'],
          ]}
        />
        <p style={p}>
          There are no standalone prompt files. Instructions are Python string
          constants. There are no multi-agent workflows beyond “call the model and
          store the reply.”
        </p>
        <h3 style={h3}>What is sent to OpenAI</h3>
        <ul style={{ ...p, paddingLeft: 20 }}>
          <li>Coach: user message plus up to five PHI <em>summary bullets</em> (not the medical file).</li>
          <li>Plans: survey profile, scores, and scraped research snippets.</li>
          <li>PHI summary job: decrypted text extract, capped (~12k characters).</li>
          <li>Group chat: message text and room topic.</li>
        </ul>
        <Callout kind="warn">
          OpenAI’s retention is governed by the OpenAI account’s data-use / ZDR
          settings, which are not configured in this repo. Assume standard API
          retention unless you enabled Zero Data Retention in the OpenAI dashboard.
        </Callout>
        <p style={p}>
          <strong>Estimated cost per user:</strong> highly variable. A light Elite
          user (a few gpt-4o coach turns) may be cents per month; seminar TTS + Whisper
          and PHI summarization add more. There is no built-in token budget or per-user
          metering.
        </p>
        <p style={p}>
          Designed but not implemented: live human expert matching, real wearable-driven
          coaching, GPT-5.2 branding on the marketing site (runtime models are gpt-4o).
        </p>
      </Section>

      <Section id="security" title="Security">
        <Table
          headers={['Control', 'Status']}
          rows={[
            ['Authentication', 'Email/password, bcrypt, JWT HS256, 7-day cookie'],
            ['Administrator security', 'Single env-configured admin; other admins demoted on boot. Password not in git'],
            ['Two-factor authentication', 'Not implemented'],
            ['Encryption', 'TLS in transit; AES-256-GCM for PHI and integration secrets; bcrypt passwords'],
            ['Firewall', 'Hostinger VPS firewall + Docker isolation. Postgres not published. App-level WAF: none'],
            ['Rate limiting', 'Not implemented'],
            ['Login lockout', 'Not implemented'],
            ['Automated-attack protection', 'None beyond Hostinger/Dokploy defaults'],
            ['Security monitoring', 'No Sentry/SIEM. PHI audit log is the only structured security log'],
            ['Logs', 'Container stdout, email_notifications, phi_audit_log, integration_audit_log'],
            ['CORS', 'allow_origins=["*"] with credentials — overly permissive'],
            ['SSRF guards', 'Admin integration URLs must be HTTPS and non-private'],
            ['Security testing', 'pytest suites (auth, PHI, integrations). No formal pentest recorded in repo'],
          ]}
        />
        <Callout kind="danger">
          Highest-priority hardening: rotate the hardcoded investor password; set
          CORS to the real website origin; add login rate limits; confirm
          PHI_MASTER_KEY and JWT_SECRET are unique strong values in Dokploy; enable
          volume backups; confirm you own the Stripe and SendGrid accounts.
        </Callout>
      </Section>

      <Section id="affiliates" title="Affiliate / E-Commerce Architecture">
        <p style={p}>
          Amazon (and generic “other”) links are <strong>manually entered</strong> on
          Admin → Affiliates. There is no Amazon Product Advertising API and no eBay
          API. Attribution is whatever query string you paste (for example
          <code> tag=yourid-20</code>). ThriveLonger does not receive Amazon conversion
          callbacks — reporting stays in Amazon Associates.
        </p>
        <p style={p}>
          Affiliate items appear in the member Store with an Amazon Partner badge and
          open the partner URL. They cannot be added to the ThriveLonger cart.
          First-party products are the 8 seed SKUs in <code>store.py</code> plus admin
          custom products; checkout can go through Stripe or the legacy unpaid cart
          endpoint.
        </p>
      </Section>

      <Section id="design" title="Design and Content">
        <Table
          headers={['Asset', 'Location', 'License notes']}
          rows={[
            ['Logo', 'frontend/public/thrivelonger-logo.png', 'Original brand mark in repo; no separate license file'],
            ['Hero and page videos', 'frontend/public/media/*.mp4 (hero, about, research, community, dashboard, insights, goals, profile)', 'Uploaded first-party clips; compress via scripts/compress_video.sh'],
            ['3D avatar', 'frontend/public/avatar-body.glb, avatar-face.glb + basis transcoder', 'TalkingHead / met4citizen-style avatar used by TalkingAvatar.jsx; confirm original license if redistributing'],
            ['Fonts', 'Google Fonts: Outfit, Work Sans, Inter (index.html)', 'SIL OFL via Google Fonts'],
            ['UI kit', 'frontend/src/components/ui/* (shadcn/Radix)', 'MIT-style shadcn components'],
            ['Colors / marketing copy', 'frontend/src/data/mockData.js', 'First-party'],
            ['HIPAA policy', 'frontend/src/data/hipaaPolicy.js + About #hipaa', 'First-party legal copy — have counsel review'],
            ['Terms', 'frontend/src/pages/TermsPage.jsx', 'First-party'],
            ['About / Info / Blog / Research', 'AboutPage, InfoPage, API-driven research/blog', 'Some About bios look duplicated/placeholder'],
            ['Figma / Canva / Adobe', 'No accounts or files referenced', 'Not in repository'],
            ['Purchased stock / licensed media', 'None declared in repo', 'If a clip or GLB was purchased, keep that receipt outside git'],
          ]}
        />
        <p style={p}>
          Page templates are React page components, not a CMS. Seminar videos uploaded
          by admins are stored in Postgres GridFS tables, not in <code>public/</code>.
        </p>
      </Section>
    </div>
  );
}
